/* CombustiblePage — módulo Control de Combustible (10-jun-2026 · Fase 1).
   Roles soportados (definidos en App.js):
     - capturista_combustible: tabs registrar, cargas-dia, subir-reporte, historial
     - jefa_capturistas: igual + resolver-alerta y cambiar-unidad
     - coordinadora_administrativa: cargas-dia, historial, reporte-ejecutivo
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import TabIncidentes from "./TabIncidentes";
import ExportarCargasModal from "./ExportarCargasModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_COMBUSTIBLE_OPTS = ["Magna", "Premium", "Diesel"];

const TABS_POR_ROL = {
  capturista_combustible: [
    { id: "registrar",       label: "Registrar carga", icon: "fa-plus" },
    { id: "cargas-dia",      label: "Cargas del día",  icon: "fa-list" },
    { id: "subir-reporte",   label: "Subir Excel Petromax", icon: "fa-file-excel" },
    { id: "historial",       label: "Historial",       icon: "fa-clock-rotate-left" },
    { id: "incidentes",      label: "Incidentes",      icon: "fa-triangle-exclamation" },
  ],
  jefa_capturistas: [
    { id: "registrar",       label: "Registrar carga", icon: "fa-plus" },
    { id: "cargas-dia",      label: "Cargas del día",  icon: "fa-list" },
    { id: "subir-reporte",   label: "Subir Excel Petromax", icon: "fa-file-excel" },
    { id: "historial",       label: "Historial",       icon: "fa-clock-rotate-left" },
    { id: "ejecutivo",       label: "Reporte ejecutivo", icon: "fa-chart-line" },
    { id: "incidentes",      label: "Incidentes",      icon: "fa-triangle-exclamation" },
  ],
  coordinadora_administrativa: [
    { id: "cargas-dia",      label: "Cargas del día",  icon: "fa-list" },
    { id: "historial",       label: "Historial",       icon: "fa-clock-rotate-left" },
    { id: "ejecutivo",       label: "Reporte ejecutivo", icon: "fa-chart-line" },
    { id: "expediente",      label: "Expediente de Unidad", icon: "fa-folder-open" },
  ],
  admin: [
    { id: "registrar",       label: "Registrar carga", icon: "fa-plus" },
    { id: "cargas-dia",      label: "Cargas del día",  icon: "fa-list" },
    { id: "subir-reporte",   label: "Subir Excel Petromax", icon: "fa-file-excel" },
    { id: "historial",       label: "Historial",       icon: "fa-clock-rotate-left" },
    { id: "ejecutivo",       label: "Reporte ejecutivo", icon: "fa-chart-line" },
    { id: "expediente",      label: "Expediente de Unidad", icon: "fa-folder-open" },
    { id: "incidentes",      label: "Incidentes",      icon: "fa-triangle-exclamation" },
  ],
};

function fmtDate(iso) {
  if (!iso) return "—";
  return iso.replace("T", " ").slice(0, 16);
}
function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      timeZone: "America/Chihuahua",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
  } catch {
    return iso.replace("T", " ").slice(0, 19);
  }
}

function PillEstatus({ valor }) {
  const map = {
    VALIDO:        { bg: "#DCFCE7", fg: "#15803D" },
    EN_REVISION:   { bg: "#FEF3C7", fg: "#92400E" },
    IRREGULARIDAD: { bg: "#FEE2E2", fg: "#B91C1C" },
  };
  const cfg = map[valor] || { bg: "#E2E8F0", fg: "#475569" };
  return <span style={{
    background: cfg.bg, color: cfg.fg, padding: "2px 8px",
    borderRadius: 999, fontWeight: 700, fontSize: "0.7rem", whiteSpace: "nowrap",
  }}>{valor || "—"}</span>;
}

function PillCruce({ valor }) {
  const map = {
    CRUZADO:    { bg: "#DCFCE7", fg: "#15803D" },
    PENDIENTE:  { bg: "#FEF3C7", fg: "#92400E" },
    SIN_CRUZAR: { bg: "#FEE2E2", fg: "#B91C1C" },
  };
  const cfg = map[valor] || { bg: "#E2E8F0", fg: "#475569" };
  return <span style={{
    background: cfg.bg, color: cfg.fg, padding: "2px 8px",
    borderRadius: 999, fontWeight: 700, fontSize: "0.7rem",
  }}>{valor || "—"}</span>;
}

export default function CombustiblePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const baseTabs = TABS_POR_ROL[user?.rol] || [];
  // 11-jun-2026 (Octavio): pestaña "Entrega de vales" solo para Fernanda y Capt2 (José Manuel).
  const VALES_USERS = ["fernanda", "capt2", "admin"];
  const tabs = VALES_USERS.includes(user?.usuario)
    ? [...baseTabs, { id: "entrega-vales", label: "Entrega de vales", icon: "fa-receipt" }]
    : baseTabs;
  const [tab, setTab] = useState(tabs[0]?.id || "");

  const puedeCapturar = ["capturista_combustible", "jefa_capturistas", "admin"].includes(user?.rol);
  const puedeResolver = ["jefa_capturistas", "coordinadora_administrativa", "admin"].includes(user?.rol);

  return (
    <div className="hub-bg min-h-screen" data-testid="page-combustible">
      <header className="topnav" style={{ flexWrap: "wrap", gap: 8 }}>
        <div className="brand">
          <div className="logo" style={{ background: "#F59E0B" }}>
            <i className="fa-solid fa-gas-pump"/>
          </div>
          <div>
            <div className="brand-title">Control de Combustible</div>
            <div className="brand-sub">Hola, {user?.nombre || user?.usuario} · {user?.rol}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginLeft: "auto" }}>
          {(user?.rol === "coordinadora_administrativa" || user?.rol === "admin") && (
            <button
              onClick={() => navigate("/admin/uso-diario")}
              data-testid="btn-ir-uso-diario"
              style={{ background: "#0F172A", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
              title="Bitácora de uso de unidades"
            >
              <i className="fa-solid fa-clipboard-list mr-1"/>Uso diario
            </button>
          )}
          <button onClick={() => navigate("/hub")} data-testid="btn-back-hub"
            style={{ background: "#334155", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            <i className="fa-solid fa-house mr-1"/>Menú
          </button>
          <button onClick={logout} data-testid="btn-logout"
            style={{ background: "#334155", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Tabs */}
        <div className="card-base p-0 overflow-hidden">
          <div className="flex overflow-x-auto border-b border-slate-200" role="tablist">
            {tabs.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className="py-3 px-4 text-sm font-bold transition-colors whitespace-nowrap"
                style={{
                  background: tab === t.id ? "#FEF3C7" : "transparent",
                  color: tab === t.id ? "#92400E" : "#475569",
                  borderBottom: tab === t.id ? "3px solid #F59E0B" : "3px solid transparent",
                }}
              >
                <i className={`fa-solid ${t.icon} mr-1.5`}/>{t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "registrar" && puedeCapturar && <TabRegistrar />}
        {tab === "cargas-dia" && <TabCargas modo="hoy" puedeResolver={puedeResolver}/>}
        {tab === "subir-reporte" && puedeCapturar && <TabSubirReporte/>}
        {tab === "historial" && <TabCargas modo="historial" puedeResolver={puedeResolver}/>}
        {tab === "ejecutivo" && <TabEjecutivo/>}
        {tab === "entrega-vales" && <TabEntregaVales/>}
        {tab === "expediente" && <TabExpedienteUnidad/>}
        {tab === "incidentes" && <TabIncidentes/>}
      </main>
    </div>
  );
}

/* ─────────────────────────── TAB 1: REGISTRAR ─────────────────────────── */
// Helper: si el nombre tiene "/" (dos resguardantes), devuelve solo el primero.
// Ej: "CARLOS CADENA CORRAL /ARTURO ARANDA" → "CARLOS CADENA CORRAL"
function primerNombre(s) {
  if (!s) return "";
  const t = String(s);
  const i = t.indexOf("/");
  return (i >= 0 ? t.slice(0, i) : t).trim();
}

// Helper: fecha de hoy en formato YYYY-MM-DD (hora local).
function hoyISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function TabRegistrar() {
  const [folioVale, setFolioVale] = useState("");
  const [folioTicket, setFolioTicket] = useState("");
  const [noEconomico, setNoEconomico] = useState("");
  const [unidad, setUnidad] = useState(null);
  const [conductor, setConductor] = useState("");
  const [noEmpleado, setNoEmpleado] = useState("");
  const [departamento, setDepartamento] = useState("");
  const [litros, setLitros] = useState("");
  const [litrosAlto, setLitrosAlto] = useState(false); // advertencia litros > 200
  const [odometro, setOdometro] = useState("");
  const [tipoComb, setTipoComb] = useState("Magna");
  const [tipoCombManual, setTipoCombManual] = useState(false); // true = capturista lo cambió a mano
  const [fechaCarga, setFechaCarga] = useState("");  // vacío por defecto; capturista escribe la fecha
  const [busy, setBusy] = useState(false);
  const [ultimas, setUltimas] = useState([]);  // últimos 5 tickets capturados

  // Octavio 16-jun-2026: dropdown si un empleado tiene varias unidades asignadas.
  // 18-jun-2026 (segunda iteración): ELIMINADO. El autocomplete por No. empleado solo
  // llena el campo "Conductor". Los demás campos se llenan a mano.

  const odometroActivo = unidad?.odometro_activo !== false;

  // Consulta el tipo de combustible más frecuente en el historial de cargas
  const autocompletarTipoComb = async (no_eco) => {
    if (!no_eco) return;
    try {
      const r = await axios.get(`${API}/combustible/unidad/${encodeURIComponent(no_eco)}/tipo-combustible-frecuente`);
      const tipo = r.data?.tipo_frecuente;
      if (tipo && ["Magna", "Premium", "Diesel"].includes(tipo)) {
        // Solo autocompletar si el capturista no lo ha cambiado manualmente
        setTipoComb(prev => {
          if (tipoCombManual) return prev; // respetar elección manual
          return tipo;
        });
      }
    } catch { /* silencioso — puede que no haya historial */ }
  };

  const aplicarUnidad = (v) => {
    setUnidad({
      descripcion:        v.descripcion,
      placa:              v.placas,
      estatus:            v.status,
      odometro_activo:    v.odometro_activo,
      conductor_actual:   v.resguardante,
      departamento_actual: v.departamento,
      no_empleado_conductor: v.no_empleado,
      tipo_combustible:   v.tipo_combustible || "Magna",
      _fuente:            v._fuente || "vehiculos_catalogo",
    });
    const eco = v.no_economico || "";
    setNoEconomico(eco);
    setConductor(primerNombre(v.resguardante || ""));
    setNoEmpleado(v.no_empleado || "");
    setDepartamento(v.departamento || "");
    if (v.odometro_activo === false) setOdometro("");
    // Autocompletar tipo combustible desde historial (sin sobreescribir elección manual)
    if (!tipoCombManual) autocompletarTipoComb(eco);
  };

  const buscarUnidad = async (valor) => {
    if (!valor.trim()) { setUnidad(null); return; }
    // 1) Buscar en empleados_vehiculos (catálogo bundleado · Octavio 18-jun-2026)
    try {
      const r = await axios.get(
        `${API}/empleados-vehiculos/por-economico/${encodeURIComponent(valor.trim())}`
      );
      aplicarUnidad({ ...r.data, _fuente: "empleados_vehiculos" });
      return;
    } catch (_) { /* sigue al fallback */ }
    // 2) Fallback a vehiculos_catalogo (catálogo principal)
    try {
      const r = await axios.get(`${API}/vehiculos/${encodeURIComponent(valor.trim())}`);
      aplicarUnidad(r.data.vehiculo);
      return;
    } catch (_) { /* sigue al fallback final */ }
    // 3) Fallback final al endpoint legacy
    try {
      const r = await axios.get(`${API}/combustible/unidad/${valor.trim()}`);
      setUnidad({ ...r.data, _fuente: "combustible" });
      setConductor(primerNombre(r.data.conductor_actual || ""));
      setNoEmpleado(r.data.no_empleado_conductor || "");
      setDepartamento(r.data.departamento_actual || "");
      if (!tipoCombManual) setTipoComb(r.data.tipo_combustible || "Magna");
      if (!tipoCombManual) autocompletarTipoComb(valor.trim());
    } catch {
      setUnidad(null);
    }
  };

  // Octavio 18-jun-2026: debounce también para No. económico (autocomplete al escribir).
  const ecoDebounceRef = useRef(null);
  const buscarUnidadDebounced = (valor) => {
    if (ecoDebounceRef.current) clearTimeout(ecoDebounceRef.current);
    const v = (valor || "").trim();
    if (v.length < 3) return;
    ecoDebounceRef.current = setTimeout(() => buscarUnidad(v), 300);
  };

  // Octavio 18-jun-2026 (segunda iteración):
  // Autocomplete por No. empleado → SOLO llena Conductor. Si no existe, no muestra error;
  // el capturista escribirá el nombre a mano y el backend lo guardará al registrar la carga.
  const empleadoDebounceRef = useRef(null);
  const buscarPorEmpleadoDebounced = (valor) => {
    if (empleadoDebounceRef.current) clearTimeout(empleadoDebounceRef.current);
    const emp = (valor || "").trim();
    if (emp.length < 3) return;
    empleadoDebounceRef.current = setTimeout(() => buscarPorEmpleado(emp), 300);
  };

  const buscarPorEmpleado = async (valor) => {
    const emp = (valor || "").trim();
    if (!emp || emp.length < 3) return;
    try {
      const r = await axios.get(`${API}/empleados/${encodeURIComponent(emp)}`);
      const nombre = primerNombre(r.data?.nombre || "");
      if (nombre && !conductor.trim()) {
        // Solo autollenamos si el capturista no escribió un nombre todavía.
        setConductor(nombre);
      } else if (nombre) {
        // Si ya hay un nombre escrito, no lo sobreescribimos pero sí podemos sugerirlo.
        setConductor(nombre);
      }
    } catch {
      // Empleado no encontrado: el capturista escribirá el nombre manualmente.
      // El backend lo guardará automáticamente al registrar la carga.
    }
  };

  // Cargar últimas 5 cargas al montar y actualizar después de guardar
  const cargarUltimas = async () => {
    try {
      const r = await axios.get(`${API}/combustible/cargas`, { params: { limit: 5 } });
      setUltimas(r.data.items || r.data || []);
    } catch { /* silencioso */ }
  };
  useEffect(() => { cargarUltimas(); }, []);

  const enviar = async (e) => {
    e.preventDefault();
    // Campos obligatorios: folio_vale, folio_ticket, no_economico, tipo_combustible, litros, odometro (si aplica), fecha_carga
    if (!folioVale.trim()) { toast.error("Folio vale es obligatorio"); return; }
    if (!folioTicket.trim()) { toast.error("No. operación ticket es obligatorio"); return; }
    if (!noEconomico.trim()) { toast.error("No. económico es obligatorio"); return; }
    if (!tipoComb) { toast.error("Tipo combustible es obligatorio"); return; }
    if (!litros) { toast.error("Litros es obligatorio"); return; }
    if (odometroActivo && !odometro) { toast.error("Odómetro es obligatorio para esta unidad"); return; }
    if (!fechaCarga) {
      toast.error("Fecha de carga es obligatoria");
      return;
    }
    if (parseFloat(litros) > 200 && !litrosAlto) {
      setLitrosAlto(true);
      toast.warning(`Litros inusualmente altos (${litros} L). Presiona «Registrar carga» de nuevo para confirmar.`);
      return;
    }
    setBusy(true);
    try {
      // Octavio 18-jun-2026: combinar la fecha elegida con la hora actual local
      // para mantener el orden cronológico de las cargas capturadas con retraso.
      const ahora = new Date();
      const hhmmss = ahora.toTimeString().slice(0, 8);  // "HH:MM:SS"
      const fecha_iso = `${fechaCarga}T${hhmmss}`;
      const body = {
        folio_vale: folioVale.trim(),
        folio_ticket_gasolinera: folioTicket.trim(),
        no_economico: noEconomico.trim(),
        no_empleado_conductor: noEmpleado.trim(),
        nombre_conductor: conductor.trim(),
        departamento: departamento.trim(),
        fecha_carga: fecha_iso,
        litros: parseFloat(litros),
        tipo_combustible: tipoComb,
        odometro: odometro ? parseInt(odometro, 10) : null,
      };
      const r = await axios.post(`${API}/combustible/carga`, body);
      const c = r.data.carga;
      toast.success(`Carga registrada (${c.estatus}). Folio ${c.folio_vale}.`);
      if (r.data.alertas?.taller) toast.error("⚠️ Unidad en TALLER — carga en revisión");
      if (r.data.alertas?.rendimiento_critico) toast.error("⚠️ Rendimiento crítico — en revisión");
      // Limpiar todos los campos para la siguiente captura
      setFolioVale(""); setFolioTicket(""); setNoEconomico(""); setUnidad(null);
      setConductor(""); setNoEmpleado(""); setDepartamento("");
      setLitros(""); setOdometro(""); setLitrosAlto(false);
      setTipoComb("Magna"); setTipoCombManual(false); setFechaCarga("");
      // Actualizar la tabla de últimas cargas
      cargarUltimas();
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  return (
    <form className="card-base p-4 space-y-3" onSubmit={enviar} data-testid="form-registrar">
      <div className="text-sm font-bold uppercase text-slate-700 mb-1">
        <i className="fa-solid fa-plus mr-2"/>Nueva carga
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo label="Folio vale *">
          <input value={folioVale} onChange={e => setFolioVale(e.target.value)} data-testid="inp-folio-vale" className="inp"/>
        </Campo>
        <Campo label="No. operación ticket *">
          <input value={folioTicket} onChange={e => setFolioTicket(e.target.value)} data-testid="inp-folio-ticket" className="inp"/>
        </Campo>
        <Campo label="No. económico *">
          <input
            value={noEconomico}
            onChange={e => { setNoEconomico(e.target.value); buscarUnidadDebounced(e.target.value); }}
            onBlur={e => buscarUnidad(e.target.value)}
            data-testid="inp-no-economico"
            placeholder="Ej. 12798"
            className="inp"
          />
        </Campo>
        <Campo label="Tipo combustible">
          <select value={tipoComb} onChange={e => { setTipoComb(e.target.value); setTipoCombManual(true); }} data-testid="sel-tipo-comb" className="inp">
            {TIPO_COMBUSTIBLE_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </Campo>
      </div>

      {unidad && (
        <div className="rounded-md p-2 text-xs" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
          <i className="fa-solid fa-circle-check mr-1 text-green-700"/>
          <strong>{unidad.descripcion}</strong> · {unidad.placa} · estatus <strong>{unidad.estatus}</strong>
          {unidad.estatus === "TALLER" && <span className="ml-2 text-red-700 font-bold">⚠️ TALLER (carga quedará EN REVISIÓN)</span>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Campo label="Litros *">
          <input
            type="number"
            step="0.001"
            inputMode="decimal"
            placeholder="45.500"
            value={litros}
            onChange={e => { setLitros(e.target.value); setLitrosAlto(false); }}
            data-testid="inp-litros"
            className="inp"
          />
          <div className="text-xs text-slate-400 mt-0.5">
            Usa punto decimal · ej: 45.500 (como aparece en el ticket)
          </div>
          {parseFloat(litros) > 200 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1">
              <i className="fa-solid fa-triangle-exclamation mr-1"/>
              {litrosAlto
                ? "Confirmado. Presiona «Registrar carga» de nuevo para guardar."
                : `Valor inusualmente alto (${litros} L). Verifica y presiona «Registrar carga» para confirmar.`}
            </div>
          )}
        </Campo>
        <Campo label={odometroActivo && unidad ? "Odómetro ★ recomendado" : "Odómetro"}>
          {odometroActivo ? (
            <input
              type="number"
              value={odometro}
              onChange={e => setOdometro(e.target.value)}
              data-testid="inp-odometro"
              className="inp"
              placeholder="km actuales del vehículo"
              style={unidad ? { borderColor: "#F59E0B", boxShadow: "0 0 0 2px #FEF3C7" } : undefined}
            />
          ) : (
            <div data-testid="inp-odometro-disabled"
                 title="Odómetro no disponible para esta unidad"
                 style={{ padding: "8px 10px", background: "#F1F5F9", border: "1px solid #CBD5E1", borderRadius: 8, color: "#94A3B8", fontSize: 13 }}>
              — <span style={{ fontSize: 11, marginLeft: 6 }}>odómetro no disponible (unidad sin odómetro funcional)</span>
            </div>
          )}
          {odometroActivo && unidad && !odometro && (
            <div className="text-xs text-amber-600 mt-0.5">
              <i className="fa-solid fa-star-half-stroke mr-1"/>Capturar el odómetro permite calcular rendimiento km/L
            </div>
          )}
        </Campo>
        <Campo label="Fecha de carga *">
          <input
            type="date"
            value={fechaCarga}
            max={hoyISO()}
            onChange={e => setFechaCarga(e.target.value)}
            data-testid="inp-fecha-carga"
            className="inp"
            required
          />
        </Campo>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Campo label="Conductor (editable)">
          <input value={conductor} onChange={e => setConductor(e.target.value)} data-testid="inp-conductor" className="inp"/>
        </Campo>
        <Campo label="No. empleado">
          <input
            value={noEmpleado}
            onChange={e => { setNoEmpleado(e.target.value); buscarPorEmpleadoDebounced(e.target.value); }}
            onBlur={e => buscarPorEmpleado(e.target.value)}
            data-testid="inp-empleado"
            placeholder="Autocompleta desde 3 dígitos"
            className="inp"
          />
        </Campo>
        <Campo label="Departamento">
          <input value={departamento} onChange={e => setDepartamento(e.target.value)} data-testid="inp-depto" className="inp"/>
        </Campo>
      </div>

      {/* Dropdown de múltiples unidades eliminado (Octavio 18-jun-2026 segunda iteración).
         El autocomplete por No. empleado solo llena el campo Conductor. */}

      {unidad && conductor && unidad.conductor_actual && conductor !== primerNombre(unidad.conductor_actual) && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          <i className="fa-solid fa-triangle-exclamation mr-1"/>
          El conductor difiere del resguardante registrado (<strong>{primerNombre(unidad.conductor_actual)}</strong>).
        </div>
      )}

      <button type="submit" disabled={busy} data-testid="btn-guardar-carga" className="btn-primary" style={{ background: "#F59E0B" }}>
        <i className="fa-solid fa-floppy-disk mr-1.5"/>{busy ? "Guardando…" : "Registrar carga"}
      </button>
      <style>{`.inp{border:1px solid #cbd5e1;border-radius:6px;padding:6px 10px;font-size:0.875rem;width:100%}`}</style>

      {/* Últimas 5 cargas capturadas — referencia rápida para evitar folios repetidos */}
      <div className="mt-6">
        <div className="text-xs font-bold uppercase text-slate-500 mb-2 flex items-center gap-2">
          <i className="fa-solid fa-clock-rotate-left"/>Últimas 5 cargas capturadas (todos los capturistas)
        </div>
        {ultimas.length === 0 ? (
          <div className="text-xs text-slate-400 italic">Sin registros aún.</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="p-2 whitespace-nowrap">Fecha</th>
                  <th className="p-2 whitespace-nowrap">Folio</th>
                  <th className="p-2 whitespace-nowrap">No. Eco</th>
                  <th className="p-2 whitespace-nowrap">Conductor</th>
                  <th className="p-2 whitespace-nowrap">Depto</th>
                  <th className="p-2 text-right whitespace-nowrap">Litros</th>
                  <th className="p-2 text-right whitespace-nowrap">Odóm</th>
                  <th className="p-2 text-right whitespace-nowrap">KM</th>
                  <th className="p-2 text-right whitespace-nowrap">KM/L</th>
                  <th className="p-2 text-right whitespace-nowrap">Importe</th>
                  <th className="p-2 whitespace-nowrap">Foto</th>
                  <th className="p-2 whitespace-nowrap">Cruce</th>
                  <th className="p-2 whitespace-nowrap">Estatus</th>
                </tr>
              </thead>
              <tbody>
                {ultimas.map((c, i) => (
                  <tr key={c.id || i} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2 whitespace-nowrap">{fmtDate(c.fecha_carga)}</td>
                    <td className="p-2 font-mono">{c.folio_vale}</td>
                    <td className="p-2 font-mono">{c.no_economico}</td>
                    <td className="p-2">{c.nombre_conductor || "—"}</td>
                    <td className="p-2">{c.departamento || "—"}</td>
                    <td className="p-2 text-right">{c.litros}</td>
                    <td className="p-2 text-right">{c.odometro || "—"}</td>
                    <td className="p-2 text-right">{c.km_recorridos ?? "—"}</td>
                    <td className="p-2 text-right">{c.rendimiento_km_l ?? "—"}</td>
                    <td className="p-2 text-right">{c.importe != null ? `$${c.importe.toFixed(2)}` : "—"}</td>
                    <td className="p-2">
                      {c.foto_odometro_url ? <a href={c.foto_odometro_url} target="_blank" rel="noreferrer"><i className="fa-solid fa-image"/></a> : "—"}
                    </td>
                    <td className="p-2"><PillCruce valor={c.cruce_gasolinera}/></td>
                    <td className="p-2"><PillEstatus valor={c.estatus}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </form>
  );
}

function Campo({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

/* ─────────────────────────── TAB 2: CARGAS DEL DÍA / HISTORIAL ─────────────────────────── */
function TabCargas({ modo, puedeResolver }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fecha, setFecha] = useState(modo === "hoy" ? new Date().toISOString().slice(0, 10) : "");
  const [filtroDepto, setFiltroDepto] = useState("");
  const [filtroNoEco, setFiltroNoEco] = useState("");
  const [filtroEmpleado, setFiltroEmpleado] = useState("");
  const [filtroEstatus, setFiltroEstatus] = useState("");
  const [showExport, setShowExport] = useState(false);
  // Botón Exportar Excel: visible para admin y coordinadora_administrativa en modo "historial"
  const puedeExportar = modo === "historial" &&
    (user?.rol === "admin" || user?.rol === "coordinadora_administrativa");

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;
      setLoading(true);
      try {
        const params = {};
        if (fecha) params.fecha = fecha;
        if (filtroDepto) params.departamento = filtroDepto;
        if (filtroNoEco) params.no_economico = filtroNoEco;
        if (filtroEmpleado) params.no_empleado = filtroEmpleado;
        if (filtroEstatus) params.estatus = filtroEstatus;
        const r = await axios.get(`${API}/combustible/cargas`, { params });
        if (active) setItems(r.data.items || []);
      } catch (e) {
        toast.error("Error: " + (e.response?.data?.detail || e.message));
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [fecha, filtroDepto, filtroNoEco, filtroEmpleado, filtroEstatus]);

  const resolver = async (id, nuevo_estatus) => {
    const nota = window.prompt(`¿Nota de resolución? (opcional)`);
    if (nota === null) return;
    try {
      await axios.put(`${API}/combustible/carga/${id}/resolver-alerta`, { nuevo_estatus, nota });
      toast.success("Resolución guardada");
      setItems(prev => prev.map(c => c.id === id ? { ...c, estatus: nuevo_estatus } : c));
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-3">
      <div className="card-base p-3 flex flex-wrap items-center gap-2">
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} data-testid="filtro-fecha" className="inp" style={{ minWidth: 140 }}/>
        <input placeholder="Departamento" value={filtroDepto} onChange={e => setFiltroDepto(e.target.value)} data-testid="filtro-depto" className="inp" style={{ minWidth: 140 }}/>
        <input placeholder="No. económico" value={filtroNoEco} onChange={e => setFiltroNoEco(e.target.value)} data-testid="filtro-no-eco" className="inp" style={{ minWidth: 130 }}/>
        <input placeholder="No. empleado" value={filtroEmpleado} onChange={e => setFiltroEmpleado(e.target.value)} data-testid="filtro-empleado" className="inp" style={{ minWidth: 130 }}/>
        <select value={filtroEstatus} onChange={e => setFiltroEstatus(e.target.value)} data-testid="filtro-estatus" className="inp" style={{ minWidth: 130 }}>
          <option value="">Todos los estatus</option>
          <option value="VALIDO">VÁLIDO</option>
          <option value="EN_REVISION">EN REVISIÓN</option>
          <option value="IRREGULARIDAD">IRREGULARIDAD</option>
        </select>
        <span className="ml-auto text-xs text-slate-600">
          {loading ? "Cargando…" : `${items.length} cargas`}
        </span>
        {puedeExportar && (
          <button type="button" onClick={() => setShowExport(true)}
                  data-testid="btn-exportar-historial"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold rounded px-3 py-2">
            <i className="fa-solid fa-file-excel mr-1"/>Exportar Excel
          </button>
        )}
      </div>

      {showExport && <ExportarCargasModal tipo="historial" onClose={() => setShowExport(false)}/>}

      <div className="card-base p-0 overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <i className="fa-solid fa-inbox text-3xl mb-2 block text-slate-300"/>Sin resultados.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="p-2">Fecha</th>
                {user?.rol === "admin" && <th className="p-2">Capturado el</th>}
                <th className="p-2">Folio</th>
                <th className="p-2">No. eco</th>
                <th className="p-2">Conductor</th>
                <th className="p-2">Depto</th>
                <th className="p-2 text-right">Litros</th>
                <th className="p-2 text-right">Odóm</th>
                <th className="p-2 text-right">Km</th>
                <th className="p-2 text-right">km/L</th>
                <th className="p-2 text-right">Importe</th>
                <th className="p-2">Foto</th>
                <th className="p-2">Cruce</th>
                <th className="p-2">Estatus</th>
                {puedeResolver && <th className="p-2">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fila-carga-${c.id}`}>
                  <td className="p-2 text-xs whitespace-nowrap">
                    {user?.rol === "coordinadora_administrativa" && c.cruce_gasolinera === "PENDIENTE"
                      ? "—"
                      : fmtDate(c.fecha_carga)}
                  </td>
                  {user?.rol === "admin" && (
                    <td className="p-2 text-xs whitespace-nowrap text-slate-400">{fmtTs(c.timestamp)}</td>
                  )}
                  <td className="p-2 mono text-xs">{c.folio_vale}</td>
                  <td className="p-2 mono text-xs">{c.no_economico}</td>
                  <td className="p-2 text-xs">{c.nombre_conductor || "—"}</td>
                  <td className="p-2 text-xs">{c.departamento || "—"}</td>
                  <td className="p-2 text-right">{c.litros}</td>
                  <td className="p-2 text-right">{c.odometro || "—"}</td>
                  <td className="p-2 text-right">{c.km_recorridos ?? "—"}</td>
                  <td className="p-2 text-right">{c.rendimiento_km_l ?? "—"}</td>
                  <td className="p-2 text-right">{c.importe != null ? `$${c.importe.toFixed(2)}` : "—"}</td>
                  <td className="p-2">
                    {c.foto_odometro_url ? <a href={c.foto_odometro_url} target="_blank" rel="noreferrer"><i className="fa-solid fa-image"/></a> : "—"}
                  </td>
                  <td className="p-2"><PillCruce valor={c.cruce_gasolinera}/></td>
                  <td className="p-2"><PillEstatus valor={c.estatus}/></td>
                  {puedeResolver && (
                    <td className="p-2">
                      {c.estatus === "EN_REVISION" && (
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => resolver(c.id, "VALIDO")}
                            data-testid={`btn-resolver-valido-${c.id}`}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded"
                          >Válido</button>
                          <button
                            type="button"
                            onClick={() => resolver(c.id, "IRREGULARIDAD")}
                            data-testid={`btn-resolver-irreg-${c.id}`}
                            className="text-xs bg-red-600 text-white px-2 py-1 rounded"
                          >Irregularidad</button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <style>{`.inp{border:1px solid #cbd5e1;border-radius:6px;padding:6px 10px;font-size:0.875rem}`}</style>
    </div>
  );
}

/* ─────────────────────────── TAB 3: SUBIR REPORTE PETROMAX ─────────────────────────── */
function TabSubirReporte() {
  const [archivo, setArchivo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultado, setResultado] = useState(null);

  const subir = async () => {
    if (!archivo) { toast.error("Selecciona un archivo Excel"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", archivo);
      const r = await axios.post(`${API}/combustible/subir-reporte-gasolinera`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResultado(r.data.reporte);
      toast.success(`Excel procesado: ${r.data.reporte.cruzados} cruzados`);
    } catch (e) {
      toast.error("Error: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="card-base p-4 space-y-3" data-testid="form-subir-reporte">
      <div className="text-sm font-bold uppercase text-slate-700">
        <i className="fa-solid fa-file-excel mr-2 text-green-700"/>Subir reporte gasolinera (Petromax)
      </div>
      <div className="text-xs text-slate-600">
        Sube el Excel quincenal. El sistema cruza los folios con las cargas registradas y reporta los faltantes.
      </div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center" style={{ background: "#FFFBEB" }}>
        <i className="fa-solid fa-cloud-arrow-up text-4xl text-amber-500 mb-2 block"/>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={e => setArchivo(e.target.files?.[0] || null)}
          data-testid="inp-archivo-excel"
          className="block mx-auto text-sm"
        />
        {archivo && <div className="text-sm mt-2 text-slate-700"><i className="fa-solid fa-file mr-1"/>{archivo.name}</div>}
      </div>
      <button
        type="button"
        onClick={subir}
        disabled={busy || !archivo}
        data-testid="btn-subir-excel"
        className="btn-primary"
        style={{ background: "#16A34A" }}
      >
        <i className="fa-solid fa-upload mr-1.5"/>{busy ? "Procesando…" : "Procesar Excel"}
      </button>

      {resultado && (
        <div className="mt-4 rounded p-3 bg-blue-50 border border-blue-200 text-sm space-y-1" data-testid="resultado-cruce">
          <div className="font-bold">Resultado del cruce</div>
          <div>Total registros en Excel: <strong>{resultado.total_registros}</strong></div>
          <div className="text-green-700"><i className="fa-solid fa-plus mr-1"/>Registros nuevos procesados: <strong>{resultado.registros_nuevos ?? resultado.cruzados}</strong></div>
          <div className="text-blue-700"><i className="fa-solid fa-rotate mr-1"/>Registros actualizados (ya existían): <strong>{resultado.registros_actualizados ?? 0}</strong></div>
          <div className="text-amber-700"><i className="fa-solid fa-triangle-exclamation mr-1"/>Registros sin cruzar: <strong>{resultado.registros_sin_cruzar ?? resultado.sin_cruzar}</strong></div>
          <div className="text-slate-600 text-xs mt-2 pt-2 border-t border-blue-200">
            <i className="fa-regular fa-clock mr-1"/>
            Último corte: <strong>{fmtDate(resultado.fecha_subida)}</strong> por {resultado.subido_por_nombre || resultado.subido_por}
          </div>
          {resultado.folios_faltantes?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-red-700">
                <i className="fa-solid fa-triangle-exclamation mr-1"/>Folios faltantes en tu sistema ({resultado.folios_faltantes.length})
              </summary>
              <ul className="mt-1 ml-4 text-xs space-y-0.5">
                {resultado.folios_faltantes.slice(0, 50).map(f => <li key={f} className="mono">{f}</li>)}
                {resultado.folios_faltantes.length > 50 && <li className="text-slate-500 italic">...y {resultado.folios_faltantes.length - 50} más</li>}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── TAB 4: REPORTE EJECUTIVO ─────────────────────────── */
function TabEjecutivo() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const puedeExportar = user?.rol === "admin" || user?.rol === "coordinadora_administrativa";

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;
      try {
        const r = await axios.get(`${API}/combustible/reporte-ejecutivo`);
        if (active) setData(r.data);
      } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <div className="card-base p-8 text-center text-sm text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-1"/>Cargando…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="tab-ejecutivo-content">
      {puedeExportar && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setShowExport(true)}
                  data-testid="btn-exportar-ejecutivo"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold rounded px-4 py-2">
            <i className="fa-solid fa-file-excel mr-1"/>Exportar Excel (con filtros)
          </button>
        </div>
      )}
      {showExport && <ExportarCargasModal tipo="ejecutivo" onClose={() => setShowExport(false)}/>}      {/* Alertas grandes */}
      {data.unidades_en_taller_con_carga?.length > 0 && (
        <div className="card-base p-4 border-2 border-red-300" style={{ background: "#FEF2F2" }}>
          <div className="font-bold text-red-700 text-base mb-1">
            <i className="fa-solid fa-triangle-exclamation mr-2"/>Unidades en TALLER con carga: {data.unidades_en_taller_con_carga.length}
          </div>
          <ul className="text-sm space-y-0.5">
            {data.unidades_en_taller_con_carga.slice(0, 10).map((u, i) => (
              <li key={i} className="mono text-xs">No. eco {u.no_economico} · Folio {u.folio_vale} · {fmtDate(u.fecha)}</li>
            ))}
          </ul>
        </div>
      )}
      {data.rendimiento_critico?.length > 0 && (
        <div className="card-base p-4 border-2 border-amber-300" style={{ background: "#FFFBEB" }}>
          <div className="font-bold text-amber-700 text-base mb-1">
            <i className="fa-solid fa-gauge-high mr-2"/>Rendimiento crítico ({data.rendimiento_critico.length} unidades)
          </div>
          <ul className="text-sm space-y-0.5">
            {data.rendimiento_critico.slice(0, 10).map((u, i) => (
              <li key={i} className="text-xs">No. eco <strong>{u.no_economico}</strong>: actual {u.rendimiento_actual} km/L vs promedio {u.promedio} km/L</li>
            ))}
          </ul>
        </div>
      )}
      {/* Métricas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric icon="fa-list" label="Total cargas" valor={data.total_cargas}/>
        <Metric icon="fa-droplet" label="Litros" valor={data.total_litros}/>
        <Metric icon="fa-coins" label="Importe" valor={`$${data.total_importe.toFixed(2)}`}/>
        <Metric icon="fa-gauge-high" label="Rendimiento avg" valor={`${data.rendimiento_promedio} km/L`}/>
      </div>

      {/* Top / peor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Listita titulo="🟢 Top rendimiento" items={data.top_rendimiento}/>
        <Listita titulo="🔴 Peor rendimiento" items={data.peor_rendimiento}/>
      </div>

      <div className="text-xs text-slate-500 space-y-1">
        <div>
          <i className="fa-solid fa-circle-info mr-1"/>
          Alertas activas (cargas en revisión): <strong>{data.alertas_activas}</strong>.
        </div>
        {data.sin_odometro_count > 0 && (
          <div className="text-slate-400">
            <i className="fa-solid fa-gauge-simple mr-1"/>
            {data.sin_odometro_count} {data.sin_odometro_count === 1 ? "unidad" : "unidades"} sin odómetro funcional, excluida{data.sin_odometro_count === 1 ? "" : "s"} del cálculo de rendimiento.
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ icon, label, valor }) {
  return (
    <div className="card-base p-3">
      <div className="text-xs text-slate-500 uppercase tracking-wide">
        <i className={`fa-solid ${icon} mr-1`}/>{label}
      </div>
      <div className="text-2xl font-bold text-slate-800 mt-1">{valor}</div>
    </div>
  );
}

function Listita({ titulo, items }) {
  return (
    <div className="card-base p-3">
      <div className="text-sm font-bold mb-2">{titulo}</div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-500">Sin datos.</div>
      ) : (
        <ol className="text-xs space-y-1">
          {items.map((u, i) => (
            <li key={i} className="flex justify-between">
              <span>{i + 1}. <strong>{u.no_economico}</strong></span>
              <span className="mono">{u.rendimiento_km_l} km/L</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}


/* ─────────────────── TabEntregaVales ───────────────────
   Solicitado 11-jun-2026 por Octavio.
   - Solo visible para fernanda y capt2 (José Manuel).
   - Formulario para registrar entrega de vales físicos por departamento.
   - Historial con filtros (folio, departamento, fecha, estado).
   - Columnas canjeados/pendientes calculadas por el backend al cruzar Petromax.
*/
const DEPARTAMENTOS_DMU = [
  "Alumbrado Público","Áreas Verdes Contratadas","Centro Histórico","Dirección DMU",
  "Fontanería","Forestación","Herrería y Obra Menor","Maquinaria Pesada y Derribos",
  "Mensajería/Administrativo","Monumentos","Parques y Jardines","Pipas","Placeros",
  "Taller","Vialidades","Vivero",
];
const ESTADO_OPCIONES = [
  { val: "ENTREGADO", label: "Entregado correctamente", icon: "✅", bg: "#DCFCE7", fg: "#15803D" },
  { val: "INCIDENTE", label: "Entregado con incidente", icon: "⚠️", bg: "#FEF3C7", fg: "#92400E" },
  { val: "PERDIDO",   label: "Lote perdido",            icon: "🔴", bg: "#FEE2E2", fg: "#B91C1C" },
  { val: "OTRO",      label: "Otro",                    icon: "📝", bg: "#E0E7FF", fg: "#3730A3" },
];

function TabEntregaVales() {
  const { user } = useAuth();
  const hoy = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    departamento: "",
    cantidad_vales: "",
    folio_inicial: "",
    folio_final: "",
    nombre_receptor: "",
    cargo_receptor: "",
    fecha_entrega: hoy,
    // Octavio 19-jun-2026: campo "estado" eliminado del formulario.
    // Las incidencias ahora se manejan en la pestaña Incidentes.
  });
  const [enviando, setEnviando] = useState(false);
  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filtros del historial (mantiene "estado" para data legacy)
  const [fq, setFq] = useState({ q: "", desde: "", hasta: "", estado: "" });

  const cargarEntregas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fq.desde) params.append("fecha_inicio", fq.desde);
      if (fq.hasta) params.append("fecha_fin", fq.hasta);
      if (fq.estado) params.append("estado", fq.estado);
      // Si el filtro luce numérico → folio. Si no → texto libre (filtramos en cliente sobre departamento/receptor)
      const isFolio = /^\d+$/.test(fq.q.trim());
      if (isFolio && fq.q.trim()) params.append("folio", fq.q.trim());
      const r = await axios.get(`${API}/combustible/entregas-vales?${params.toString()}`);
      let arr = r.data?.entregas || [];
      // Filtro local por texto libre (depto / receptor)
      if (!isFolio && fq.q.trim()) {
        const needle = fq.q.trim().toLowerCase();
        arr = arr.filter(e =>
          (e.departamento || "").toLowerCase().includes(needle) ||
          (e.nombre_receptor || "").toLowerCase().includes(needle)
        );
      }
      setEntregas(arr);
    } catch {
      toast.error("No se pudo cargar el historial");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      await Promise.resolve();
      if (!active) return;
      await cargarEntregas();
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fq]);

  const fiNum = parseInt(form.folio_inicial, 10);
  const ffNum = parseInt(form.folio_final, 10);
  const totalFolios = (isFinite(fiNum) && isFinite(ffNum)) ? (ffNum - fiNum + 1) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.departamento) return toast.error("Selecciona un departamento");
    if (!form.cantidad_vales || parseInt(form.cantidad_vales, 10) <= 0) return toast.error("Cantidad inválida");
    if (!form.folio_inicial || !form.folio_final) return toast.error("Folios obligatorios");
    if (totalFolios !== null && totalFolios < 1) return toast.error("Folio final debe ser >= folio inicial");
    if (!form.nombre_receptor.trim()) return toast.error("Nombre del receptor obligatorio");

    setEnviando(true);
    try {
      await axios.post(`${API}/combustible/entrega-vales`, {
        ...form,
        estado: "ENTREGADO",  // Octavio 19-jun-2026: siempre ENTREGADO desde el formulario
        cantidad_vales: parseInt(form.cantidad_vales, 10),
      });
      toast.success(`Entrega registrada · ${form.departamento} · ${totalFolios} folios`);
      setForm({
        departamento: "", cantidad_vales: "", folio_inicial: "", folio_final: "",
        nombre_receptor: "", cargo_receptor: "", fecha_entrega: hoy,
      });
      cargarEntregas();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar");
    } finally { setEnviando(false); }
  };

  const setF = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  // Botones imprimir: capturista_combustible (Fernanda, capt2/Jose Manuel) y admin (Octavio 19-jun-2026)
  const puedeImprimir = user?.rol === "admin" || user?.rol === "capturista_combustible";

  const imprimirRelacion = async (f) => {
    if (!f.departamento || !f.folio_inicial || !f.folio_final) {
      return toast.error("Completa departamento y rango de folios primero");
    }
    try {
      const params = new URLSearchParams({
        departamento: f.departamento,
        folio_inicial: f.folio_inicial,
        folio_final: f.folio_final,
        entregado_por: f.nombre_receptor || "—",
        fecha_emision: f.fecha_entrega || hoy,
      });
      const r = await axios.get(`${API}/combustible/entrega-vales/pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF Relación pendiente — en construcción");
    }
  };
  const imprimirBitacoras = async (f) => {
    if (!f.folio_inicial || !f.folio_final) {
      return toast.error("Completa el rango de folios primero");
    }
    try {
      const params = new URLSearchParams({
        folio_inicial: f.folio_inicial,
        folio_final: f.folio_final,
      });
      const r = await axios.get(`${API}/combustible/bitacoras-vale/pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF Bitácoras pendiente — en construcción");
    }
  };

  return (
    <div className="space-y-4" data-testid="tab-entrega-vales-content">
      {/* ── FORMULARIO ─────────────────────────────────────── */}
      <div className="card-base p-4 bg-white">
        <h3 className="text-base font-bold text-slate-800 mb-3">
          <i className="fa-solid fa-receipt mr-2 text-amber-600"/>Registrar entrega de vales
        </h3>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="form-entrega-vales">
          {/* Departamento */}
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Departamento receptor *</span>
            <select required value={form.departamento} onChange={setF("departamento")}
                    className="w-full border border-slate-300 rounded px-2 py-2 mt-1"
                    data-testid="ev-departamento">
              <option value="">— selecciona —</option>
              {DEPARTAMENTOS_DMU.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          {/* Cantidad */}
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Cantidad de vales *</span>
            <input type="number" min="1" required value={form.cantidad_vales} onChange={setF("cantidad_vales")}
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1" data-testid="ev-cantidad"/>
          </label>
          {/* Folio inicial */}
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Folio inicial *</span>
            <input required value={form.folio_inicial} onChange={setF("folio_inicial")}
                   placeholder="1733100"
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1" data-testid="ev-folio-ini"/>
          </label>
          {/* Folio final */}
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Folio final *</span>
            <input required value={form.folio_final} onChange={setF("folio_final")}
                   placeholder="1733199"
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1" data-testid="ev-folio-fin"/>
          </label>
          {totalFolios !== null && (
            <div className="md:col-span-2 text-xs" data-testid="ev-total-calculado">
              {totalFolios < 1 ? (
                <span className="text-red-700 font-bold">⚠️ Folio final debe ser mayor o igual al inicial</span>
              ) : (
                <span className="text-emerald-700 font-bold">Total de folios: {totalFolios}</span>
              )}
            </div>
          )}
          {/* Receptor */}
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Nombre receptor *</span>
            <input required value={form.nombre_receptor} onChange={setF("nombre_receptor")}
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1" data-testid="ev-receptor"/>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Cargo receptor (opcional)</span>
            <input value={form.cargo_receptor} onChange={setF("cargo_receptor")}
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1" data-testid="ev-cargo"/>
          </label>
          {/* Fecha */}
          <label className="block">
            <span className="text-xs font-bold text-slate-600 uppercase">Fecha de entrega *</span>
            <input type="date" required value={form.fecha_entrega} onChange={setF("fecha_entrega")}
                   className="w-full border border-slate-300 rounded px-2 py-2 mt-1" data-testid="ev-fecha"/>
          </label>
          {/* Octavio 19-jun-2026: campo "Estado" y "Comentario del incidente" eliminados.
              Incidencias ahora se manejan en la pestaña "Incidentes". */}
          {/* Submit */}
          <div className="md:col-span-2 flex justify-between items-center pt-2 gap-2">
            {puedeImprimir && (
              <div className="flex gap-2">
                <button type="button"
                        onClick={() => imprimirRelacion(form)}
                        className="bg-slate-700 hover:bg-slate-800 text-white text-sm font-bold rounded px-3 py-2"
                        data-testid="ev-btn-imprimir-relacion">
                  <i className="fa-solid fa-print mr-1"/>Imprimir relación
                </button>
                <button type="button"
                        onClick={() => imprimirBitacoras(form)}
                        className="bg-slate-700 hover:bg-slate-800 text-white text-sm font-bold rounded px-3 py-2"
                        data-testid="ev-btn-imprimir-bitacoras">
                  <i className="fa-solid fa-file-pdf mr-1"/>Imprimir bitácoras individuales
                </button>
              </div>
            )}
            <button type="submit" disabled={enviando}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold rounded px-4 py-2 disabled:opacity-60"
                    data-testid="ev-submit">
              <i className="fa-solid fa-paper-plane mr-1"/> {enviando ? "Guardando…" : "Registrar entrega"}
            </button>
          </div>
        </form>
      </div>

      {/* ── HISTORIAL ──────────────────────────────────────── */}
      <div className="card-base p-4 bg-white">
        <h3 className="text-base font-bold text-slate-800 mb-3">
          <i className="fa-solid fa-clock-rotate-left mr-2 text-slate-500"/>Historial de entregas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <input placeholder="Buscar folio, depto o receptor"
                 value={fq.q} onChange={(e) => setFq(p => ({...p, q: e.target.value}))}
                 className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="ev-filtro-q"/>
          <input type="date" value={fq.desde} onChange={(e) => setFq(p => ({...p, desde: e.target.value}))}
                 className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="ev-filtro-desde"/>
          <input type="date" value={fq.hasta} onChange={(e) => setFq(p => ({...p, hasta: e.target.value}))}
                 className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="ev-filtro-hasta"/>
          <select value={fq.estado} onChange={(e) => setFq(p => ({...p, estado: e.target.value}))}
                  className="border border-slate-300 rounded px-2 py-2 text-sm" data-testid="ev-filtro-estado">
            <option value="">Todos los estados</option>
            {ESTADO_OPCIONES.map(o => <option key={o.val} value={o.val}>{o.icon} {o.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 py-4">Cargando…</div>
        ) : entregas.length === 0 ? (
          <div className="text-sm text-slate-500 py-4" data-testid="ev-empty">Sin entregas registradas.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>
                  <th className="px-2 py-2 text-left">Fecha</th>
                  <th className="px-2 py-2 text-left">Departamento</th>
                  <th className="px-2 py-2 text-right">Cant.</th>
                  <th className="px-2 py-2 text-left">Rango folios</th>
                  <th className="px-2 py-2 text-left">Recibió</th>
                  <th className="px-2 py-2 text-left">Registró</th>
                  <th className="px-2 py-2 text-center">Canj.</th>
                  <th className="px-2 py-2 text-center">Pend.</th>
                  <th className="px-2 py-2 text-center">Estado</th>
                  <th className="px-2 py-2 text-center">Coment.</th>
                </tr>
              </thead>
              <tbody>
                {entregas.map(e => {
                  const est = ESTADO_OPCIONES.find(o => o.val === e.estado) || ESTADO_OPCIONES[0];
                  return (
                    <tr key={e.id} className="border-t border-slate-100" data-testid={`ev-row-${e.id}`}>
                      <td className="px-2 py-2">{e.fecha_entrega}</td>
                      <td className="px-2 py-2">{e.departamento}</td>
                      <td className="px-2 py-2 text-right">{e.cantidad_vales}</td>
                      <td className="px-2 py-2 mono">{e.folio_inicial} – {e.folio_final}</td>
                      <td className="px-2 py-2">{e.nombre_receptor}{e.cargo_receptor ? <span className="text-slate-400"> · {e.cargo_receptor}</span> : null}</td>
                      <td className="px-2 py-2">{e.registrado_por}</td>
                      <td className="px-2 py-2 text-center font-bold text-emerald-700">{e.canjeados ?? 0}</td>
                      <td className={`px-2 py-2 text-center font-bold ${e.alerta_pendiente_canje ? "text-red-700" : "text-amber-700"}`}>
                        {e.pendientes ?? e.total_folios}
                        {e.alerta_pendiente_canje && <i className="fa-solid fa-triangle-exclamation ml-1" title="Vence: lleva más de 30 días sin canjear"/>}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span style={{ background: est.bg, color: est.fg, padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
                          {est.icon} {est.label}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        {e.comentario ? (
                          <i className="fa-solid fa-comment text-blue-600" title={e.comentario}/>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   TAB: EXPEDIENTE DE UNIDAD — solo coordinadora_administrativa
   Octavio 15-jun-2026: ficha + resguardos + cargas + exportar Excel.
   ═══════════════════════════════════════════════════════════════ */
const DEPARTAMENTOS_EXPEDIENTE = [
  "MONUMENTOS", "PLACEROS", "PARQUES Y JARDINES", "OBRA MENOR",
  "ÁREAS VERDES CONTRATADAS", "VIVERO", "CENTRO ACOPIO", "CENTRO HISTÓRICO",
  "TRABAJOS ESPECIALES", "ALUMBRADO PÚBLICO", "PIPAS", "HERRERÍA",
  "MAQUINARIA", "PODA Y DERRIBOS",
];

function TabExpedienteUnidad() {
  const [filtroDepto, setFiltroDepto] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [unidadSel, setUnidadSel] = useState(null); // {no_economico,...}
  const [data, setData] = useState(null); // expediente completo
  const [filtroRango, setFiltroRango] = useState({ desde: "", hasta: "" });

  // Autocompletado en tiempo real
  useEffect(() => {
    if (!busqueda || busqueda.trim().length < 2) {
      setSugerencias([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/vehiculos`, {
          params: { q: busqueda.trim(), limite: 12, departamento: filtroDepto || undefined },
        });
        setSugerencias(r.data.items || []);
      } catch {
        setSugerencias([]);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [busqueda, filtroDepto]);

  const seleccionar = async (v) => {
    setBusqueda(`${v.no_economico} · ${v.descripcion || ""}`);
    setSugerencias([]);
    setUnidadSel(v);
    try {
      const r = await axios.get(`${API}/vehiculos/${encodeURIComponent(v.no_economico)}/expediente`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando expediente");
      setData(null);
    }
  };

  const exportarExpediente = async () => {
    if (!unidadSel) return;
    try {
      const r = await axios.get(
        `${API}/vehiculos/${encodeURIComponent(unidadSel.no_economico)}/expediente/excel`,
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `expediente_${unidadSel.no_economico}_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.click();
      toast.success("Expediente descargado (3 hojas)");
    } catch {
      toast.error("Error al exportar expediente");
    }
  };

  const exportarPDF = async () => {
    if (!unidadSel) return;
    try {
      const params = {};
      if (filtroRango.desde) params.desde = filtroRango.desde;
      if (filtroRango.hasta) params.hasta = filtroRango.hasta;
      const r = await axios.get(
        `${API}/vehiculos/${encodeURIComponent(unidadSel.no_economico)}/expediente/pdf`,
        { params, responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `expediente_${unidadSel.no_economico}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.click();
      toast.success("Reporte PDF descargado");
    } catch {
      toast.error("Error al generar PDF");
    }
  };

  const cargasFiltradas = useMemo(() => {
    if (!data) return [];
    let arr = data.cargas_combustible || [];
    if (filtroRango.desde) arr = arr.filter((c) => (c.fecha_carga || "") >= filtroRango.desde);
    if (filtroRango.hasta) arr = arr.filter((c) => (c.fecha_carga || "") <= filtroRango.hasta + "T23:59:59");
    return arr;
  }, [data, filtroRango]);

  const totales = useMemo(() => {
    if (!cargasFiltradas.length) return { litros: 0, importe: 0, kml: null };
    const litros = cargasFiltradas.reduce((s, c) => s + (parseFloat(c.litros) || 0), 0);
    const importe = cargasFiltradas.reduce((s, c) => s + (parseFloat(c.importe) || 0), 0);
    const cKml = cargasFiltradas.filter((c) => c.rendimiento_km_l);
    const kml = cKml.length ? cKml.reduce((s, c) => s + parseFloat(c.rendimiento_km_l), 0) / cKml.length : null;
    return { litros, importe, kml };
  }, [cargasFiltradas]);

  return (
    <div data-testid="tab-expediente-unidad" className="space-y-4">
      {/* Buscador + Export button (siempre visible) */}
      <div className="card-base p-4">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <div className="text-sm font-bold uppercase text-slate-700">
            <i className="fa-solid fa-magnifying-glass mr-2"/>Buscar unidad
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={exportarExpediente}
              disabled={!unidadSel}
              data-testid="exp-exportar-top"
              title={unidadSel ? "Descargar Excel con 3 hojas (Ficha · Resguardos · Cargas)" : "Selecciona una unidad primero"}
              style={{
                background: unidadSel ? "#15803D" : "#94A3B8",
                color: "white", border: 0,
                padding: "8px 14px", borderRadius: 8,
                fontSize: 13, fontWeight: 700,
                cursor: unidadSel ? "pointer" : "not-allowed",
              }}
            >
              <i className="fa-solid fa-file-arrow-down mr-2"/>Exportar expediente Excel
            </button>
            <button
              onClick={exportarPDF}
              disabled={!unidadSel}
              data-testid="exp-exportar-pdf"
              title={unidadSel ? "Reporte ejecutivo PDF con filtros aplicados" : "Selecciona una unidad primero"}
              style={{
                background: unidadSel ? "#DC2626" : "#94A3B8",
                color: "white", border: 0,
                padding: "8px 14px", borderRadius: 8,
                fontSize: 13, fontWeight: 700,
                cursor: unidadSel ? "pointer" : "not-allowed",
              }}
            >
              <i className="fa-solid fa-file-pdf mr-2"/>Reporte PDF
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "stretch" }}>
          <select value={filtroDepto} onChange={(e) => setFiltroDepto(e.target.value)}
                  data-testid="exp-filtro-depto"
                  style={{
                    flex: "0 1 260px",
                    minWidth: 220,
                    height: 44,
                    background: "white",
                    border: "1px solid #E2E8F0",
                    borderRadius: 10,
                    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                    padding: "0 36px 0 14px",
                    fontSize: 13.5,
                    color: "#1E293B",
                    appearance: "none",
                    WebkitAppearance: "none",
                    backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2364748B'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z' clip-rule='evenodd'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    backgroundSize: 16,
                    cursor: "pointer",
                  }}>
            <option value="">Todos los departamentos</option>
            {DEPARTAMENTOS_EXPEDIENTE.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <div style={{ position: "relative", flex: "1 1 320px", minWidth: 260 }}>
            <i className="fa-solid fa-magnifying-glass" style={{
              position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
              color: "#94A3B8", fontSize: 13, pointerEvents: "none",
            }}/>
            <input value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setUnidadSel(null); }}
                   placeholder="No. Económico, placas, No. Empleado, nombre conductor…"
                   data-testid="exp-buscar"
                   style={{
                     width: "100%",
                     height: 44,
                     boxSizing: "border-box",
                     background: "white",
                     border: "1px solid #E2E8F0",
                     borderRadius: 10,
                     boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
                     padding: "0 14px 0 38px",
                     fontSize: 13.5,
                     color: "#1E293B",
                   }}/>
          {sugerencias.length > 0 && (
            <ul style={{ position: "absolute", left: 0, right: 0, top: "100%", background: "white", border: "1px solid #CBD5E1", borderRadius: 8, marginTop: 4, maxHeight: 280, overflowY: "auto", zIndex: 10, listStyle: "none", padding: 0 }}>
              {sugerencias.map((v) => (
                <li key={v.no_economico} onClick={() => seleccionar(v)}
                    data-testid={`exp-sugerencia-${v.no_economico}`}
                    style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #F1F5F9", fontSize: 13 }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "white"}>
                  <strong>{v.no_economico}</strong> · {v.descripcion} {v.placas && <span style={{ color: "#64748B" }}>· {v.placas}</span>}
                  <div style={{ fontSize: 11, color: "#64748B" }}>
                    {v.departamento || "Sin departamento"} · {v.resguardante || "Sin resguardante"}
                  </div>
                </li>
              ))}
            </ul>
          )}
          </div>
        </div>
      </div>

      {!data && unidadSel === null && (
        <div className="card-base p-8 text-center text-slate-500" data-testid="exp-empty">
          <i className="fa-solid fa-folder-open" style={{ fontSize: 36, color: "#CBD5E1", display: "block", marginBottom: 8 }}/>
          Escribe en el buscador para localizar una unidad.
        </div>
      )}

      {data && (
        <>
          {/* Ficha actual */}
          <div className="card-base p-4" data-testid="exp-ficha">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold uppercase text-slate-700">
                <i className="fa-solid fa-id-card mr-2"/>Ficha actual
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
              <Ficha label="No. Económico" valor={data.vehiculo.no_economico} bold/>
              <Ficha label="No. Activo" valor={data.vehiculo.no_activo}/>
              <Ficha label="Descripción" valor={data.vehiculo.descripcion}/>
              <Ficha label="Marca" valor={data.vehiculo.marca}/>
              <Ficha label="Modelo" valor={data.vehiculo.modelo}/>
              <Ficha label="Placas" valor={data.vehiculo.placas} mono/>
              <Ficha label="Departamento" valor={data.vehiculo.departamento}/>
              <Ficha label="Resguardante" valor={data.vehiculo.resguardante}/>
              <Ficha label="No. Empleado" valor={data.vehiculo.no_empleado}/>
              <Ficha label="Status">
                <PillEstatus valor={data.vehiculo.status || "ACTIVO"}/>
              </Ficha>
              <Ficha label="Odómetro">
                <span style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: data.vehiculo.odometro_activo ? "#D1FAE5" : "#FEE2E2", color: data.vehiculo.odometro_activo ? "#047857" : "#991B1B" }}>
                  <i className={`fa-solid ${data.vehiculo.odometro_activo ? "fa-gauge" : "fa-gauge-simple"}`}/> {data.vehiculo.odometro_activo ? "ON" : "OFF"}
                </span>
              </Ficha>
              <Ficha label="Última actualización" valor={`${(data.vehiculo.ultima_actualizacion || "").slice(0,16).replace("T", " ")} · ${data.vehiculo.actualizado_por || ""}`}/>
            </div>
          </div>

          {/* Historial de resguardos */}
          <div className="card-base p-4" data-testid="exp-resguardos">
            <div className="text-sm font-bold uppercase text-slate-700 mb-2">
              <i className="fa-solid fa-clock-rotate-left mr-2"/>Historial de resguardos
              <span style={{ marginLeft: 8, color: "#64748B", fontSize: 11 }}>
                ({(data.historial_resguardos || []).length} movimiento{(data.historial_resguardos || []).length === 1 ? "" : "s"})
              </span>
            </div>
            {(data.historial_resguardos || []).length === 0 ? (
              <div className="text-slate-400 py-4 text-center text-sm">Sin movimientos registrados.</div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                <table className="w-full" style={{ fontSize: 12, borderCollapse: "collapse" }}>
                  <thead style={{ background: "#F1F5F9", position: "sticky", top: 0 }}>
                    <tr>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left">Campo</th>
                      <th className="p-2 text-left">Anterior</th>
                      <th className="p-2 text-left">Nuevo</th>
                      <th className="p-2 text-left">Modificado por</th>
                      <th className="p-2 text-left">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.historial_resguardos || []).map((h, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2 text-xs whitespace-nowrap">{(h.fecha_cambio || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="p-2"><strong>{h.campo_modificado}</strong></td>
                        <td className="p-2 text-slate-400">{h.valor_anterior || "—"}</td>
                        <td className="p-2 text-emerald-700 font-bold">{h.valor_nuevo || "—"}</td>
                        <td className="p-2">{h.modificado_por}</td>
                        <td className="p-2 text-xs text-slate-500">{h.motivo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Conductores que han cargado combustible a esta unidad */}
          <SeccionConductoresCombustible cargas={data.cargas_combustible || []} />

          {/* Historial de cargas */}
          <div className="card-base p-4" data-testid="exp-cargas">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-sm font-bold uppercase text-slate-700">
                <i className="fa-solid fa-gas-pump mr-2"/>Historial de cargas
                <span style={{ marginLeft: 8, color: "#64748B", fontSize: 11 }}>
                  ({cargasFiltradas.length} carga{cargasFiltradas.length === 1 ? "" : "s"})
                </span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="date" value={filtroRango.desde}
                       onChange={(e) => setFiltroRango((r) => ({ ...r, desde: e.target.value }))}
                       data-testid="exp-rango-desde"
                       className="inp" style={{ width: 150 }}/>
                <input type="date" value={filtroRango.hasta}
                       onChange={(e) => setFiltroRango((r) => ({ ...r, hasta: e.target.value }))}
                       data-testid="exp-rango-hasta"
                       className="inp" style={{ width: 150 }}/>
              </div>
            </div>
            {cargasFiltradas.length === 0 ? (
              <div className="text-slate-400 py-4 text-center text-sm">Sin cargas en ese rango.</div>
            ) : (
              <>
                <div style={{ maxHeight: 360, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                  <table className="w-full" style={{ fontSize: 12, borderCollapse: "collapse" }}>
                    <thead style={{ background: "#F1F5F9", position: "sticky", top: 0 }}>
                      <tr>
                        <th className="p-2 text-left">Fecha</th>
                        {user?.rol === "admin" && <th className="p-2 text-left">Capturado el</th>}
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Conductor</th>
                        <th className="p-2 text-right">Litros</th>
                        <th className="p-2 text-right">Odóm</th>
                        <th className="p-2 text-right">KM</th>
                        <th className="p-2 text-right">KM/L</th>
                        <th className="p-2 text-right">Importe</th>
                        <th className="p-2 text-left">Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cargasFiltradas.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100">
                          <td className="p-2 text-xs whitespace-nowrap">
                            {user?.rol === "coordinadora_administrativa" && c.cruce_gasolinera === "PENDIENTE"
                              ? "—"
                              : fmtDate(c.fecha_carga)}
                          </td>
                          {user?.rol === "admin" && (
                            <td className="p-2 text-xs whitespace-nowrap text-slate-400">{fmtTs(c.timestamp)}</td>
                          )}
                          <td className="p-2 mono text-xs">{c.folio_vale}</td>
                          <td className="p-2 text-xs">{c.nombre_conductor || "—"}</td>
                          <td className="p-2 text-right">{c.litros}</td>
                          <td className="p-2 text-right">{c.odometro || "—"}</td>
                          <td className="p-2 text-right">{c.km_recorridos ?? "—"}</td>
                          <td className="p-2 text-right">{c.rendimiento_km_l ?? "—"}</td>
                          <td className="p-2 text-right">{c.importe != null ? `$${c.importe.toFixed(2)}` : "—"}</td>
                          <td className="p-2"><PillEstatus valor={c.estatus}/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Totales */}
                <div style={{ marginTop: 10, display: "flex", gap: 16, padding: 10, background: "#F8FAFC", borderRadius: 8, flexWrap: "wrap" }}
                     data-testid="exp-totales">
                  <div><strong>Total litros:</strong> {totales.litros.toFixed(2)}</div>
                  <div><strong>Total importe:</strong> ${totales.importe.toFixed(2)}</div>
                  <div><strong>Promedio KM/L:</strong> {totales.kml ? totales.kml.toFixed(2) : "—"}</div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SeccionConductoresCombustible({ cargas }) {
  const conductores = useMemo(() => {
    const mapa = {}; // key → { nombre, cargas, ultimaFecha }
    for (const c of cargas) {
      const nombre = (c.nombre_conductor || "").trim();
      const key = nombre || "__sin_conductor__";
      const fecha = c.fecha_carga || "";
      if (!mapa[key]) {
        mapa[key] = { nombre: nombre || null, cargas: 0, ultimaFecha: fecha };
      }
      mapa[key].cargas += 1;
      if (fecha > mapa[key].ultimaFecha) mapa[key].ultimaFecha = fecha;
    }
    return Object.values(mapa).sort((a, b) => b.cargas - a.cargas);
  }, [cargas]);

  if (conductores.length === 0) return null;

  return (
    <div className="card-base p-4" data-testid="exp-conductores-combustible">
      <div className="text-sm font-bold uppercase text-slate-700 mb-3">
        <i className="fa-solid fa-users mr-2"/>Conductores que han cargado combustible
        <span style={{ marginLeft: 8, color: "#64748B", fontSize: 11 }}>
          ({conductores.length} conductor{conductores.length === 1 ? "" : "es"} distinto{conductores.length === 1 ? "" : "s"})
        </span>
      </div>
      <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
        <table className="w-full" style={{ fontSize: 12, borderCollapse: "collapse" }}>
          <thead style={{ background: "#F1F5F9" }}>
            <tr>
              <th className="p-2 text-left" style={{ fontWeight: 700, color: "#475569" }}>Conductor</th>
              <th className="p-2 text-right" style={{ fontWeight: 700, color: "#475569" }}>Cargas</th>
              <th className="p-2 text-left" style={{ fontWeight: 700, color: "#475569" }}>Última carga</th>
            </tr>
          </thead>
          <tbody>
            {conductores.map((c, i) => (
              <tr key={i} className="border-t border-slate-100"
                  style={{ background: i % 2 === 0 ? "white" : "#FAFAFA" }}>
                <td className="p-2" style={{ color: c.nombre ? "#0F172A" : "#94A3B8", fontStyle: c.nombre ? "normal" : "italic" }}>
                  {c.nombre || "Sin conductor especificado"}
                </td>
                <td className="p-2 text-right">
                  <span style={{
                    display: "inline-block",
                    minWidth: 32,
                    padding: "2px 10px",
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: 11,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                  }}>
                    {c.cargas}
                  </span>
                </td>
                <td className="p-2 text-xs text-slate-500">
                  {c.ultimaFecha ? c.ultimaFecha.slice(0, 10) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Ficha({ label, valor, children, bold, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "#64748B", fontWeight: 800, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
      {children || (
        <div style={{ fontSize: 13, color: "#0F172A", fontWeight: bold ? 800 : 600, fontFamily: mono ? "monospace" : "inherit" }}>
          {valor || "—"}
        </div>
      )}
    </div>
  );
}
